import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, CalendarDaysIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * Lo que se ofrece al gestor **después** de tipificar.
 *
 * 🔴 Esto es la otra mitad del rediseño del 22/10/2026, y sin ella la lista de
 * tipificaciones nueva no se sostiene. Antes la tipificación hacía dos cosas a
 * la vez —describir lo que pasó y decidir si se volvía a llamar—, y como
 * «Volver a llamar» era la única que no cerraba el caso, se llevaba el 91,9 % de
 * los clics. Ahora la tipificación solo describe, y **lo que viene después se
 * decide aquí, explícitamente**.
 *
 * Eso cierra además un hueco del diseño original: `payment_promise` no creaba
 * seguimiento **a propósito**, porque automatizarlo sin fecha ni importe sería
 * inventarse el plazo. La fecha la pone ahora quien habló con el deudor.
 *
 * ⚠️ **No bloquea la jornada.** Se puede ignorar y seguir: la siguiente llamada
 * entra igual. Si cerrar esto fuera obligatorio, costaría los segundos que el
 * rediseño quiere ahorrar.
 */

/** Mañana a las 10:00, que es el plazo que alguien elige el 90 % de las veces. */
function porDefecto(ahora = new Date()) {
  const d = new Date(ahora);
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  // `datetime-local` quiere hora LOCAL sin zona, así que no sirve `toISOString`,
  // que convierte a UTC y adelanta o atrasa el plazo una o dos horas.
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const AccionesTrasTipificar = ({ acciones, onCrearTarea, onCancelarProgramada, onCerrar }) => {
  const [cuando, setCuando] = useState(porDefecto);
  const [creando, setCreando] = useState(false);
  const [programadaCancelada, setProgramadaCancelada] = useState(false);

  if (!acciones) return null;
  const { debtorName, expedientId, nextAssignmentId } = acciones;

  const crear = async () => {
    if (!cuando || !expedientId) return;
    setCreando(true);
    const ok = await onCrearTarea({ expedientId, date: new Date(cuando).toISOString() });
    setCreando(false);
    if (ok) onCerrar();
  };

  const cancelar = async () => {
    const ok = await onCancelarProgramada(nextAssignmentId);
    if (ok) setProgramadaCancelada(true);
  };

  return (
    <div className='rounded-lg border border-blue-200 bg-blue-50 p-3'>
      <div className='mb-2 flex items-start justify-between gap-2'>
        <p className='text-sm text-blue-900'>
          Tipificada{debtorName ? <span className='font-medium'> · {debtorName}</span> : null}
        </p>
        <button
          type='button'
          onClick={onCerrar}
          aria-label='Cerrar'
          className='rounded-lg p-1 text-blue-700 transition hover:bg-blue-100'
        >
          <XMarkIcon className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>

      {/* Solo cuando la tipificación ha programado algo — hoy, «Salta el buzón». */}
      {nextAssignmentId && !programadaCancelada && (
        <div className='mb-2 flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2'>
          <span className='text-sm text-gray-700'>Se ha programado otra llamada</span>
          <button
            type='button'
            onClick={cancelar}
            className='flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-800 transition hover:bg-red-100'
          >
            <TrashIcon className='h-4 w-4' aria-hidden='true' />
            Cancelar
          </button>
        </div>
      )}
      {programadaCancelada && <p className='mb-2 text-sm text-gray-600'>Llamada cancelada.</p>}

      <div className='flex flex-wrap items-center gap-2'>
        <input
          type='datetime-local'
          value={cuando}
          onChange={(e) => setCuando(e.target.value)}
          className='rounded-lg border border-gray-300 px-2 py-1.5 text-sm'
          aria-label='Cuándo'
        />
        <button
          type='button'
          disabled={creando || !expedientId}
          onClick={crear}
          className='flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
        >
          <CalendarDaysIcon className='h-4 w-4' aria-hidden='true' />
          Crear tarea
        </button>

        {expedientId && (
          <Link
            to={`/view/${expedientId}`}
            target='_blank'
            rel='noreferrer'
            className='flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50'
          >
            <ArrowTopRightOnSquareIcon className='h-4 w-4' aria-hidden='true' />
            Ir al expediente
          </Link>
        )}
      </div>
    </div>
  );
};

export { porDefecto };
export default AccionesTrasTipificar;
